/**
 * InboxPage — "Send to seity" entry point at /inbox.
 *
 * V0 surface (WEB-APP-INBOX-V0-1): paste a URL, transcript, or freeform note,
 * optionally pick a lens (`nate`, `strategic`, `marketing-input`, or none),
 * submit. The engine writes the content into a per-user-per-lens Communication
 * OS thread; no agent processes it yet (V1 work).
 *
 * Aesthetic: utilitarian dev dashboard — dense, every control reachable, no
 * atmospheric framing. Mirrors the `.sp-*` design system used across
 * Seitiate+. See `~/Dev/seitiate-plus/CLAUDE.md` aesthetic discipline.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { AlertCircle, Inbox, Loader2, RefreshCw, Send } from 'lucide-react';
import {
  inbox as inboxApi,
  type InboxMessageRow,
  type InboxSubmitResponse,
} from '@/api/engine';

// Lens options visible in the picker.  `''` is the "(no lens)" option and is
// normalized server-side to the literal token `general`.
const LENS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: '(no lens)' },
  { value: 'nate', label: 'nate' },
  { value: 'strategic', label: 'strategic' },
  { value: 'marketing-input', label: 'marketing-input' },
];

interface LastSubmission {
  thread_id: string;
  thread_name: string;
  lens: string;
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const deltaMs = Date.now() - date.getTime();
  const seconds = Math.round(deltaMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function InboxPage() {
  const [content, setContent] = useState('');
  const [lens, setLens] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSubmission, setLastSubmission] = useState<LastSubmission | null>(null);

  const [recent, setRecent] = useState<InboxMessageRow[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);

  const refreshRecent = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const rows = await inboxApi.listRecent(10);
      setRecent(rows);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load recent submissions';
      setRecentError(message);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshRecent();
  }, [refreshRecent]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const response: InboxSubmitResponse = await inboxApi.submit({
        content: trimmed,
        lens: lens || null,
        source_url: sourceUrl.trim() || null,
      });
      setLastSubmission({
        thread_id: response.thread_id,
        thread_name: response.thread_name,
        lens: response.lens,
      });
      setContent('');
      setSourceUrl('');
      // Don't reset the lens — likely the user has more of the same thing.
      void refreshRecent();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submit failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = content.trim().length > 0 && !submitting;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '12px 4px 32px' }}>
      <header style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Inbox size={18} aria-hidden />
        <h1
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          Inbox — Send to seity
        </h1>
      </header>
      <p
        style={{
          margin: '0 0 16px',
          color: 'var(--sp-text-muted)',
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        Paste a URL, transcript, or freeform note. Submissions land in a Communication OS
        thread keyed by your user and the chosen lens. No agent processes them yet — that's
        V1 work (FAMILI-INVOCATION-FROM-THREAD-1).
      </p>

      <section className="sp-widget" style={{ marginBottom: 16 }}>
        <div className="sp-widget-header">
          <span>Compose</span>
          {lastSubmission && (
            <span style={{ color: 'var(--sp-ok)', textTransform: 'none', letterSpacing: 0 }}>
              Filed to thread {lastSubmission.thread_name}
            </span>
          )}
        </div>
        <form className="sp-widget-body" onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label className="sp-label" htmlFor="inbox-content">
              Content
            </label>
            <textarea
              id="inbox-content"
              className="sp-input"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Paste a URL, transcript snippet, or freeform note…"
              rows={6}
              disabled={submitting}
              style={{ resize: 'vertical', fontFamily: 'var(--sp-font-mono, ui-monospace, SFMono-Regular, monospace)', fontSize: 13 }}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '160px 1fr',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div>
              <label className="sp-label" htmlFor="inbox-lens">
                Lens
              </label>
              <select
                id="inbox-lens"
                className="sp-input"
                value={lens}
                onChange={(event) => setLens(event.target.value)}
                disabled={submitting}
              >
                {LENS_OPTIONS.map((option) => (
                  <option key={option.value || 'no-lens'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label" htmlFor="inbox-source-url">
                Source URL (optional)
              </label>
              <input
                id="inbox-source-url"
                className="sp-input"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder="https://…"
                disabled={submitting}
                inputMode="url"
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--sp-error)',
                fontSize: 12,
                marginBottom: 8,
              }}
            >
              <AlertCircle size={14} aria-hidden />
              <span>{error}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="submit"
              className="sp-btn sp-btn-primary"
              disabled={!canSubmit}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              <span>{submitting ? 'Sending…' : 'Send to seity'}</span>
            </button>
            <span style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>
              Cmd/Ctrl+Enter to submit
            </span>
          </div>
        </form>
      </section>

      <section className="sp-widget">
        <div className="sp-widget-header">
          <span>Recent submissions</span>
          <button
            type="button"
            className="sp-btn"
            onClick={() => void refreshRecent()}
            disabled={recentLoading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 8px',
              fontSize: 11,
            }}
            aria-label="Refresh recent submissions"
          >
            <RefreshCw size={11} className={recentLoading ? 'animate-spin' : undefined} />
            <span>Refresh</span>
          </button>
        </div>
        <div className="sp-widget-body">
          {recentError && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--sp-error)',
                fontSize: 12,
                marginBottom: 8,
              }}
            >
              <AlertCircle size={14} aria-hidden />
              <span>{recentError}</span>
            </div>
          )}
          {!recentLoading && recent.length === 0 && !recentError && (
            <div className="sp-empty" style={{ padding: '24px 12px', fontSize: 13 }}>
              No submissions yet.
            </div>
          )}
          {recent.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {recent.map((row) => (
                <li
                  key={row.message_id}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--sp-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span
                      style={{
                        fontFamily: 'var(--sp-font-mono, ui-monospace, SFMono-Regular, monospace)',
                        fontSize: 11,
                        color: 'var(--sp-text-muted)',
                      }}
                    >
                      {row.thread_name ?? row.thread_id.slice(0, 8)} · {row.lens}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
                      {formatRelativeTime(row.created_at)}
                    </span>
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--sp-text)' }}>
                    {row.content_preview}
                  </span>
                  {row.source_url && (
                    <a
                      href={row.source_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{ fontSize: 11, color: 'var(--sp-accent)' }}
                    >
                      {row.source_url}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

export default InboxPage;
